/**
 * Vorlage „Würfeln“.
 *
 * Draufsicht auf ein Schneidebrett. Die Zutat liegt mit der Schnittfläche nach
 * unten, bei der Zwiebel zeigt die Wurzel nach rechts. Das Messer schneidet
 * zuerst längs bis kurz vor die Wurzel und dann quer. Mit jedem Querschnitt
 * löst sich eine Reihe Würfel und rückt ein Stück ab; das Wurzelende bleibt
 * übrig. So sieht man, warum die Zwiebel beim Würfeln zusammenhält.
 */
import { ease, lerp, random, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { roundedRectPath } from '../paths.ts';
import { compact, faded, group, line, rotated } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { cutSchedule, divisions, piece } from '../parts/cutting.ts';
import { KNIFE_BLADE, board, knife } from '../parts/knife.ts';
import { mixPose } from '../parts/pose.ts';
import type { Pose } from '../parts/pose.ts';
import { PRODUCE_NAMES, produce } from '../parts/produce.ts';
import type { ProduceKind } from '../parts/produce.ts';
import { arrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Lage der Zutat auf dem Brett. */
const BOX = { x: 56, y: 42, width: 120, height: 50 } as const;

/** Größe des Messers; bei den Querschnitten bleibt der Griff so im Bild. */
const KNIFE_SCALE = 0.82;

/** Klingenlänge in dieser Vorlage. */
const BLADE = KNIFE_BLADE * KNIFE_SCALE;

/** Sichtbare Klingenbreite, solange das Messer aufrecht schneidet. */
const CUTTING_SQUASH = 0.5;

/**
 * Die Zutat wird mit der Wurzel rechts gebaut und gespiegelt gezeigt: Für
 * Rechtshänder liegt die Wurzel links, die Messerspitze zeigt auf sie.
 */
const MIRROR_X = BOX.x + BOX.width / 2;

/** Spiegelt eine x-Koordinate aus dem Bauraum in die Anzeige. */
function mirror(x: number): number {
  return 2 * MIRROR_X - x;
}

/** Ruhelage des Messers oben rechts. */
const PARK: Pose = { x: 176, y: 128, rotate: -4, lift: 0.6 };

/** Zeitplan der Schleife. */
const TIME = {
  appear: [0, 0.05],
  approach: [0.04, 0.1],
  lengthwise: [0.1, 0.4],
  turn: [0.4, 0.46],
  crosswise: [0.46, 0.84],
  park: [0.84, 0.9],
  spread: [0.85, 0.92],
  vanish: [0.94, 1],
} as const;

/** Raster je Würfelgröße. */
const GRID = {
  small: { rows: 5, columns: 8 },
  medium: { rows: 4, columns: 6 },
  large: { rows: 3, columns: 4 },
} as const;

/** Fortschritt innerhalb eines Zeitabschnitts. */
function phase(t: number, range: readonly [number, number]): number {
  return segment(t, range[0], range[1]);
}

/** Geometrie eines Durchlaufs. */
interface Layout {
  readonly xs: readonly number[];
  readonly ys: readonly number[];
  readonly cutEnd: number;
  readonly crossCuts: number;
}

/** Pose des Messers zu einem Zeitpunkt. */
function knifePose(t: number, layout: Layout): Pose {
  const { xs, ys, cutEnd, crossCuts } = layout;
  const lengthwiseCount = ys.length - 2;
  // Längsschnitte: Die Spitze endet kurz vor der Wurzel.
  const lengthPose = (index: number, lift: number): Pose => ({
    x: mirror(cutEnd) + BLADE,
    y: ys[index + 1] ?? BOX.y,
    rotate: 0,
    lift,
  });
  // Querschnitte: Die Spitze zeigt vom Koch weg (nach oben), die Klinge liegt
  // quer über der Zutat, der Griff bleibt auf dem Brett.
  const crossPose = (index: number, lift: number): Pose => ({
    x: mirror(xs[index + 1] ?? cutEnd),
    y: BOX.y + BOX.height + 4,
    rotate: 90,
    lift,
  });

  if (t < TIME.lengthwise[0]) {
    return mixPose(PARK, lengthPose(0, 1), ease.inOutCubic(phase(t, TIME.approach)));
  }
  if (t < TIME.turn[0]) {
    const state = cutSchedule(t, TIME.lengthwise[0], TIME.lengthwise[1], lengthwiseCount);
    const from = lengthPose(Math.max(0, state.index - 1), 1);
    const to = lengthPose(state.index, state.lift);
    return { ...to, y: lerp(from.y, to.y, state.travel) };
  }
  if (t < TIME.crosswise[0]) {
    return mixPose(lengthPose(lengthwiseCount - 1, 1), crossPose(0, 1), ease.inOutCubic(phase(t, TIME.turn)));
  }
  if (t < TIME.park[0]) {
    const state = cutSchedule(t, TIME.crosswise[0], TIME.crosswise[1], crossCuts);
    const from = crossPose(Math.max(0, state.index - 1), 1);
    const to = crossPose(state.index, state.lift);
    return { ...to, x: lerp(from.x, to.x, state.travel) };
  }
  return mixPose(crossPose(crossCuts - 1, 1), PARK, ease.inOutCubic(phase(t, TIME.park)));
}

/** Zahl der fertigen Schnitte in einem Abschnitt. */
function doneCuts(t: number, range: readonly [number, number], count: number): number {
  if (t < range[0]) {
    return 0;
  }
  if (t >= range[1]) {
    return count;
  }
  return cutSchedule(t, range[0], range[1], count).done;
}

/** Zutat in Stücken, im Bauraum (Wurzel rechts). */
function ingredient(t: number, kind: ProduceKind, size: keyof typeof GRID): SceneNode[] {
  const grid = GRID[size];
  const item = produce(kind, BOX);
  const hasRoot = item.keepRight >= 4;
  const right = BOX.x + BOX.width;
  const cutEnd = hasRoot ? right - item.keepRight : right;
  const ys = divisions(BOX.y, BOX.y + BOX.height, grid.rows);
  const xs = divisions(BOX.x, cutEnd, grid.columns);
  const crossCuts = hasRoot ? grid.columns : grid.columns - 1;
  const lengthDone = doneCuts(t, TIME.lengthwise, grid.rows - 1);
  const crossDone = doneCuts(t, TIME.crosswise, crossCuts);

  // Ohne Wurzel löst sich mit dem letzten Schnitt auch die letzte Spalte.
  const freeColumns = !hasRoot && crossDone === crossCuts ? grid.columns : crossDone;
  const spread = ease.outCubic(phase(t, TIME.spread));
  const crossLength = TIME.crosswise[1] - TIME.crosswise[0];

  const nodes: MaybeNode[] = [];
  for (let column = 0; column < freeColumns; column += 1) {
    const cutIndex = Math.min(column, crossCuts - 1);
    const freedAt = TIME.crosswise[0] + ((cutIndex + 0.5) / crossCuts) * crossLength;
    const loosen = ease.outBack(segment(t, freedAt, freedAt + 0.05));
    for (let row = 0; row < grid.rows; row += 1) {
      const centre = row - (grid.rows - 1) / 2;
      const cell = { x0: xs[column] ?? 0, x1: xs[column + 1] ?? 0, y0: ys[row] ?? 0, y1: ys[row + 1] ?? 0 };
      // Am Ende rücken die Würfel auseinander und verdrehen sich etwas, damit
      // sie wie lose Stücke wirken und nicht wie ein Gitter.
      const rng = random(column * 31 + row * 7 + 3);
      const jitterX = (rng() - 0.5) * 6;
      const jitterY = (rng() - 0.5) * 6;
      const turn = (rng() - 0.5) * 44;
      const dx = -6 * loosen + (-(grid.columns - column) * 3.2 + jitterX) * spread;
      const dy = centre * (1.5 * loosen + 4.5 * spread) + jitterY * spread;
      const cx = (cell.x0 + cell.x1) / 2;
      const cy = (cell.y0 + cell.y1) / 2;
      nodes.push(rotated(turn * spread, cx + dx, cy + dy, [piece(item, cell, dx, dy, true)]));
    }
  }

  // Noch zusammenhängender Rest mit den Längsschnitten.
  const restStart = xs[Math.min(freeColumns, grid.columns)] ?? cutEnd;
  if (freeColumns < grid.columns || hasRoot) {
    const restCell = { x0: restStart, x1: right + 12, y0: BOX.y - 12, y1: BOX.y + BOX.height + 12 };
    const cutStyle = { stroke: 'ink', strokeWidth: STROKE.detail } as const;
    const marks: MaybeNode[] = ys
      .slice(1, 1 + lengthDone)
      .map((y) => line(BOX.x - 4, y, cutEnd, y, cutStyle));
    if (crossDone > 0) {
      marks.push(line(restStart, BOX.y - 4, restStart, BOX.y + BOX.height + 4, cutStyle));
    }
    nodes.push(
      group([piece(item, restCell, 0, 0, false), group(marks, { clip: item.outline })], {
        clip: roundedRectPath(
          restCell.x0,
          restCell.y0,
          restCell.x1 - restCell.x0,
          restCell.y1 - restCell.y0,
          0,
        ),
      }),
    );
  }
  return compact(nodes);
}

/** Lage der Schnitte für die Messerführung. */
function layoutOf(kind: ProduceKind, size: keyof typeof GRID): Layout {
  const grid = GRID[size];
  const item = produce(kind, BOX);
  const hasRoot = item.keepRight >= 4;
  const right = BOX.x + BOX.width;
  const cutEnd = hasRoot ? right - item.keepRight : right;
  return {
    xs: divisions(BOX.x, cutEnd, grid.columns),
    ys: divisions(BOX.y, BOX.y + BOX.height, grid.rows),
    cutEnd,
    crossCuts: hasRoot ? grid.columns : grid.columns - 1,
  };
}

/** Vorlage „Würfeln“. */
export const cutDiceTemplate = defineTemplate({
  id: 'cut.dice',
  group: 'cut',
  title: 'Würfeln',
  description:
    'Die Zutat wird erst längs bis kurz vor die Wurzel eingeschnitten und dann quer in gleichmäßige Würfel geschnitten.',
  durationSeconds: 8,
  keywords: ['würfel', 'gewürfelt', 'brunoise', 'klein schneiden', 'kleingeschnitten'],
  stillTime: 0.9,
  parameters: [
    choice('ingredient', 'Zutat', PRODUCE_NAMES, 'onion'),
    choice('size', 'Würfelgröße', { small: 'fein', medium: 'mittel', large: 'grob' }, 'small'),
  ],
  inferValues(text) {
    return { ...ingredientFromText(text), ...sizeFromText(text) };
  },
  scene(values, t) {
    const kind = textValue(values, 'ingredient', 'onion') as ProduceKind;
    const size = textValue(values, 'size', 'small') as keyof typeof GRID;
    const appear = ease.outCubic(phase(t, TIME.appear));
    const vanish = 1 - ease.inCubic(phase(t, TIME.vanish));
    const pieces = group(ingredient(t, kind, size), {
      transform: { scaleX: -1, ox: MIRROR_X, oy: 0 },
    });
    // Beim Schneiden steht die Klinge aufrecht, in der Ruhelage liegt sie flach.
    const upright = ease.inOutCubic(phase(t, TIME.approach)) - ease.inOutCubic(phase(t, TIME.park));
    return compact([
      ...board(),
      faded(Math.min(appear, vanish), [pieces]),
      knife({
        ...knifePose(t, layoutOf(kind, size)),
        scale: KNIFE_SCALE,
        squash: lerp(1, CUTTING_SQUASH, upright),
      }),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 168, y: 104 },
        { x: 120, y: 107 },
        { x: 72, y: 104 },
      ]),
      arrow([
        { x: 196, y: 46 },
        { x: 196, y: 88 },
      ]),
    ];
  },
});

/** Zutat aus dem Schritttext. */
export function ingredientFromText(text: string): Record<string, string> {
  const lower = text.toLowerCase();
  if (lower.includes('kartoffel')) return { ingredient: 'potato' };
  if (lower.includes('möhre') || lower.includes('karotte') || lower.includes('möhren'))
    return { ingredient: 'carrot' };
  if (lower.includes('gurke')) return { ingredient: 'cucumber' };
  if (lower.includes('zwiebel') || lower.includes('schalotte')) return { ingredient: 'onion' };
  return {};
}

/** Schnittgröße aus dem Schritttext. */
export function sizeFromText(text: string): Record<string, string> {
  const lower = text.toLowerCase();
  if (lower.includes('fein')) return { size: 'small' };
  if (lower.includes('grob')) return { size: 'large' };
  return {};
}
